// src/utils/calendarUtils.js
//
// Web calendar helpers. Mirrors the conventions used in mobile/src/utils/calendarUtils.js
// so the user-facing experience is consistent across platforms.
//
// Key design choices:
// - All elections are added as ALL-DAY events. We never invent a fake start/end
//   time, so users don't see "Start: Jun 1, End: Jun 2" for an election day.
// - For Google we open the prefilled "render?action=TEMPLATE" URL in a new tab
//   so the user lands on Google's event-creation screen and confirms — same UX
//   as native iOS/Android in the mobile app. We no longer auto-insert via the
//   Google Calendar API (which required OAuth and silently saved the event).
// - For Outlook we use the deeplink with bare YYYY-MM-DD dates and `allday=true`.
// - For iCal we generate a spec-compliant `.ics` blob with `DTEND;VALUE=DATE`
//   set to the day AFTER the start date (the iCal all-day convention).

/**
 * Strip any time component to YYYY-MM-DD, then return YYYYMMDD (Google-friendly).
 */
const formatYmdCompact = (dateString) => {
  if (!dateString) return null;
  return dateString.split("T")[0].replace(/-/g, "");
};

/**
 * Return YYYY-MM-DD with no time component.
 */
const formatYmdDashed = (dateString) => {
  if (!dateString) return null;
  return dateString.split("T")[0];
};

/**
 * Get the day after `dateString` in YYYYMMDD (compact) format.
 * Used for the *exclusive* end-date that Google Calendar / iCal expect for
 * all-day events.
 */
const getNextDayCompact = (dateString) => {
  if (!dateString) return null;
  try {
    // Append T00:00:00 so the date is parsed in local time, not UTC, which
    // prevents an off-by-one day shift around midnight UTC.
    const date = new Date(formatYmdDashed(dateString) + "T00:00:00");
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
  } catch {
    return null;
  }
};

/**
 * Get the day after `dateString` in YYYY-MM-DD format (Outlook-friendly).
 */
const getNextDayDashed = (dateString) => {
  const compact = getNextDayCompact(dateString);
  if (!compact) return null;
  return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
};

/**
 * Build the Google Calendar prefilled-event URL.
 *
 * `dates=YYYYMMDD/YYYYMMDD` is the all-day format; the second date is
 * exclusive, so for a single-day election it must be the next day.
 */
export const getGoogleCalendarLink = (election) => {
  const startCompact = formatYmdCompact(election.election_date);
  if (!startCompact) return null;
  const endCompact = getNextDayCompact(election.election_date) || startCompact;

  const title = encodeURIComponent(election.election_name || "Election Day");
  const details = encodeURIComponent(
    (election.election_notes || "") +
      "\n\nFind your polling place, check voter registration, and learn about absentee and early voting at:\n" +
      "https://www.nass.org/Can-I-Vote\n" +
      "https://ballotpedia.org"
  );
  const location = encodeURIComponent(
    election.location || "Polling location not available"
  );

  return (
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${title}` +
    `&dates=${startCompact}/${endCompact}` +
    `&details=${details}` +
    `&location=${location}`
  );
};

/**
 * Open Google Calendar's prefilled-event page in a new tab so the user can
 * review and confirm. Returns the URL it opened (matches the previous async
 * signature so callers can `await` and `window.open(returnedLink)` without
 * changes — but we open it ourselves first to avoid popup-blockers swallowing
 * the click).
 */
export const addElectionToGoogleCalendar = async (election) => {
  const link = getGoogleCalendarLink(election);
  if (!link) {
    throw new Error("Could not build a Google Calendar link for this election.");
  }
  // Returning the link lets existing callers (CalendarModal) decide how to
  // open it; they currently call `window.open(link, "_blank", "noopener,noreferrer")`.
  return link;
};

/**
 * Outlook deeplink. Uses bare YYYY-MM-DD dates and `allday=true` so Outlook
 * shows the event spanning the full day rather than a 0-minute slot.
 */
export const getOutlookCalendarLink = (election) => {
  const startDashed = formatYmdDashed(election.election_date);
  if (!startDashed) return null;
  const endDashed = getNextDayDashed(election.election_date) || startDashed;

  const title = encodeURIComponent(election.election_name || "Election Day");
  const body = encodeURIComponent(
    (election.election_notes || "") +
      "\n\nVisit https://www.nass.org/Can-I-Vote for details."
  );

  return (
    "https://outlook.live.com/calendar/0/deeplink/compose" +
    "?path=/calendar/action/compose&rru=addevent" +
    `&subject=${title}` +
    `&startdt=${startDashed}` +
    `&enddt=${endDashed}` +
    "&allday=true" +
    `&body=${body}`
  );
};

/**
 * Escape a value for an iCalendar TEXT property.
 * https://datatracker.ietf.org/doc/html/rfc5545#section-3.3.11
 */
const escapeIcsText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/\\/g, "\\\\") // backslashes first
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
};

/**
 * Format a Date as YYYYMMDDTHHMMSSZ (iCal datetime-utc).
 */
const formatIcsDateTimeUtc = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  const ss = String(date.getUTCSeconds()).padStart(2, "0");
  return `${y}${m}${d}T${hh}${mm}${ss}Z`;
};

/**
 * Fold long iCal lines to <=75 octets per line, per RFC 5545 section 3.1.
 */
const foldIcsLine = (line) => {
  if (line.length <= 75) return line;
  const parts = [];
  let remaining = line;
  while (remaining.length > 75) {
    parts.push(remaining.slice(0, 75));
    remaining = " " + remaining.slice(75);
  }
  if (remaining.length > 0) parts.push(remaining);
  return parts.join("\r\n");
};

/**
 * Build a spec-compliant `.ics` payload for a single all-day election event.
 * The previous version had leading whitespace on every line (broken iCal).
 */
const buildIcsContent = (election) => {
  const startCompact = formatYmdCompact(election.election_date);
  if (!startCompact) return null;
  // For all-day VEVENTs, DTEND is exclusive — set it to the next day.
  const endCompact = getNextDayCompact(election.election_date) || startCompact;

  const dtstamp = formatIcsDateTimeUtc(new Date());
  const summary = escapeIcsText(election.election_name || "Election Day");
  const description = escapeIcsText(
    (election.election_notes || "") +
      "\n\nVisit https://www.nass.org/Can-I-Vote for details."
  );
  const location = escapeIcsText(
    election.location || "Polling location not available"
  );
  const uid = `${Date.now()}-${election.id || "election"}@civixthread.com`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CivixThread//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `CREATED:${dtstamp}`,
    `DTSTART;VALUE=DATE:${startCompact}`,
    `DTEND;VALUE=DATE:${endCompact}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    "STATUS:CONFIRMED",
    "SEQUENCE:0",
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return lines.map(foldIcsLine).join("\r\n") + "\r\n";
};

export const downloadIcsFile = (election) => {
  const icsContent = buildIcsContent(election);
  if (!icsContent) {
    throw new Error("Could not build calendar file for this election.");
  }

  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const safeName =
    (election.election_name || "election").replace(/[^a-z0-9]/gi, "_") || "election";
  link.href = url;
  link.download = `${safeName}-event.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Free the blob URL on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
